import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { layout, type AppPageWidth } from "./tokens";

type PageContentProps = {
  width?: AppPageWidth;
  children: React.ReactNode;
};

export function PageContent({ width = "content", children }: PageContentProps) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: layout.page.maxWidth[width],
        mx: "auto",
        px: layout.page.paddingX,
        py: layout.page.paddingY,
      }}
    >
      {children}
    </Box>
  );
}

type PageHeaderProps = {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
};

export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "flex-start" }}
      spacing={2}
      sx={{
        mb: layout.pageHeader.contentBottomGap,
        "@media print": { display: "none" },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="h4" fontWeight={700}>
          {title}
        </Typography>

        {subtitle ? (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: layout.pageHeader.titleBottomGap }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {actions ? (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: { xs: "stretch", md: "flex-end" },
            alignSelf: { xs: "stretch", md: "flex-start" },
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Stack>
  );
}

type SectionCardProps = {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
};

export function SectionCard({
  title,
  subtitle,
  children,
  actions,
}: SectionCardProps) {
  return (
    <Card
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: layout.card.radius,
      }}
    >
      <CardContent sx={{ p: layout.card.padding }}>
        <Stack spacing={layout.section.gap}>
          {(title || subtitle) && (
            <Box>
              {title ? (
                <Typography variant="h6" fontWeight={700}>
                  {title}
                </Typography>
              ) : null}

              {subtitle ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
          )}

          {children}
        </Stack>
      </CardContent>

      {actions ? (
        <>
          <Divider />
          <Box sx={{ px: layout.card.padding, py: 2 }}>{actions}</Box>
        </>
      ) : null}
    </Card>
  );
}

type SummaryStatCardProps = {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  subtext?: React.ReactNode;
  footer?: React.ReactNode;
  accentColor?: string;
  sx?: SxProps<Theme>;
};

export function SummaryStatCard({
  label,
  value,
  icon,
  subtext,
  footer,
  accentColor = "primary.main",
  sx,
}: SummaryStatCardProps) {
  return (
    <Card
      elevation={0}
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: layout.card.radius,
        ...sx,
      }}
    >
      <CardContent sx={{ p: layout.card.padding }}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {icon ? (
              <Box sx={{ color: accentColor, display: "flex", flexShrink: 0 }}>
                {icon}
              </Box>
            ) : null}

            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {label}
            </Typography>
          </Stack>

          <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
            {value}
          </Typography>

          {subtext ? (
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          ) : null}

          {footer ? <Box>{footer}</Box> : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
